import React from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';

const MAX_WIDTH = 400;

const CustomMultiSelectDropdown = ({
    items = [],
    disabled = false,
    onSelectedItemsChange,
    selectedItems = [],
    type = ""
}) => {
    const { t } = useTranslation();

    const colors = {
        green:       '#86BC25',
        green_dark:  '#5C8F1A',
        green_pale:  '#E4EDD6',
        green_xpale: '#EEF4E2',
        page_bg:     '#F4F7EE',
        white:       '#FFFFFF',
        navy:        '#1A2410',
        text_sub:    '#637A45',
        text_muted:  '#8A9E6A',
        border:      '#E4EDD6',
    };

    const options = items.map(item => ({
        value: item.id,
        label: item.name,
    }));

    const selectedOptions = options.filter(option =>
        selectedItems.includes(option.value)
    );

    const handleChange = (selected) => {
        const ids = selected ? selected.map(opt => opt.value) : [];
        onSelectedItemsChange(ids);
    };

    const customStyles = {
        // ── Outer control box ──
        control: (base, state) => ({
            ...base,
            backgroundColor: disabled ? colors.green_xpale : colors.white,
            borderWidth: 1.5,
            borderColor: state.isFocused ? colors.green : colors.border,
            borderRadius: 10,
            boxShadow: state.isFocused
                ? '0 0 0 3px rgba(134, 188, 37, 0.10)'
                : '0 2px 8px rgba(92, 143, 26, 0.06)',
            minHeight: 44,
            opacity: disabled ? 0.6 : 1,
            maxWidth: MAX_WIDTH,
            width: '100%',
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'border-color 0.2s, box-shadow 0.2s',
            '&:hover': {
                borderColor: colors.green,
            },
        }),

        // ── Value container (chips + placeholder) ──
        valueContainer: (base) => ({
            ...base,
            padding: '6px 12px',
            gap: 6,
            flexWrap: 'wrap',
        }),

        // ── Placeholder text ──
        placeholder: (base) => ({
            ...base,
            color: colors.text_muted,
            fontSize: 14,
            fontWeight: 500,
            fontFamily: 'Plus Jakarta Sans, sans-serif',
        }),

        // ── Input while typing ──
        input: (base) => ({
            ...base,
            color: colors.navy,
            fontSize: 14,
            fontFamily: 'Plus Jakarta Sans, sans-serif',
        }),

        // ── Multi value chip (tag) ──
        multiValue: (base) => ({
            ...base,
            backgroundColor: colors.green_xpale,
            borderRadius: 100,
            border: `1px solid ${colors.green_pale}`,
            paddingLeft: 4,
            paddingRight: 2,
        }),

        // ── Chip label ──
        multiValueLabel: (base) => ({
            ...base,
            color: colors.green_dark,
            fontSize: 12,
            fontWeight: 700,
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            paddingLeft: 6,
            paddingRight: 4,
        }),

        // ── Chip × remove button ──
        multiValueRemove: (base) => ({
            ...base,
            color: colors.text_muted,
            borderRadius: '0 100px 100px 0',
            paddingRight: 6,
            cursor: 'pointer',
            ':hover': {
                backgroundColor: colors.green_pale,
                color: colors.green_dark,
            },
        }),

        // ── Dropdown arrow area ──
        indicatorsContainer: (base) => ({
            ...base,
            paddingRight: 6,
        }),
        dropdownIndicator: (base, state) => ({
            ...base,
            color: state.isFocused ? colors.green_dark : colors.text_muted,
            padding: '0 6px',
            transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s, color 0.2s',
            ':hover': { color: colors.green_dark },
        }),
        clearIndicator: (base) => ({
            ...base,
            color: colors.text_muted,
            padding: '0 4px',
            cursor: 'pointer',
            ':hover': { color: '#B91C1C' },
        }),
        indicatorSeparator: (base) => ({
            ...base,
            backgroundColor: colors.border,
            marginTop: 8,
            marginBottom: 8,
        }),

        // ── Dropdown menu ──
        menu: (base) => ({
            ...base,
            backgroundColor: colors.white,
            border: `1.5px solid ${colors.border}`,
            borderRadius: 12,
            boxShadow: '0 8px 24px rgba(92, 143, 26, 0.12)',
            zIndex: 9999,
            maxWidth: MAX_WIDTH,
            width: '100%',
            overflow: 'hidden',
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            marginTop: 6,
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
            maxWidth: MAX_WIDTH,
            fontFamily: 'Plus Jakarta Sans, sans-serif',
        }),
        menuList: (base) => ({
            ...base,
            padding: 6,
        }),

        // ── Each option row ──
        option: (base, state) => ({
            ...base,
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            fontSize: 14,
            fontWeight: state.isSelected ? 700 : 500,
            backgroundColor: state.isSelected
                ? colors.green_xpale
                : state.isFocused
                    ? colors.green_xpale
                    : colors.white,
            color: state.isSelected
                ? colors.green_dark
                : state.isFocused
                    ? colors.green_dark
                    : colors.navy,
            borderRadius: 8,
            cursor: 'pointer',
            padding: '9px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            // Checkmark for selected items
            ':before': state.isSelected ? {
                content: '"✓"',
                color: colors.green_dark,
                fontWeight: 800,
                marginRight: 4,
            } : {},
        }),

        // ── No options message ──
        noOptionsMessage: (base) => ({
            ...base,
            color: colors.text_muted,
            fontSize: 13,
            fontStyle: 'italic',
            padding: '10px 12px',
        }),
    };

    const getPlaceholder = () => {
        if (!selectedItems.length) return t('common:pickAnItem');
        if (selectedItems.length === 1) return t('common:oneItemSelected', { count: 1 }) || '1 item selected';
        return t('common:multipleItemsSelected', { count: selectedItems.length }) || `${selectedItems.length} items selected`;
    };

    return (
        <div style={{
            margin: 10,
            maxWidth: MAX_WIDTH,
            width: '100%',
        }}>
            <Select
                isMulti
                isDisabled={disabled}
                options={options}
                value={selectedOptions}
                onChange={handleChange}
                styles={customStyles}
                placeholder={getPlaceholder()}
                closeMenuOnSelect={false}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                noOptionsMessage={({ inputValue }) =>
                    t('common:noOptionsFound', { inputValue }) || `No result found for "${inputValue}"`
                }
            />
        </div>
    );
};

export default CustomMultiSelectDropdown;